import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { adminGetContactSubmissions, adminGetBookings } from '../api/adminApi';

interface AdminContextValue {
  newInquiriesCount: number;
  updatedInquiriesCount: number;
  pendingBookingsCount: number;
  refreshInquiriesCount: () => void;
}

const AdminContext = createContext<AdminContextValue>({
  newInquiriesCount: 0,
  updatedInquiriesCount: 0,
  pendingBookingsCount: 0,
  refreshInquiriesCount: () => {},
});

export const AdminContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [newInquiriesCount, setNewInquiriesCount] = useState(0);
  const [updatedInquiriesCount, setUpdatedInquiriesCount] = useState(0);
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);

  const refreshInquiriesCount = useCallback(async () => {
    try {
      const submissions = await adminGetContactSubmissions();
      setNewInquiriesCount(submissions.filter((s) => s.status === 'new').length);
      setUpdatedInquiriesCount(submissions.filter((s) => s.status === 'updated').length);
    } catch {
      // silently fail — don't block admin UI if fetch errors
    }
  }, []);

  const refreshPendingBookingsCount = useCallback(async () => {
    try {
      const from = new Date().toISOString();
      const to = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
      const bookings = await adminGetBookings(from, to, 'pending');
      setPendingBookingsCount(bookings.length);
    } catch {
      // silently fail — don't block admin UI if fetch errors (e.g. booking feature not yet configured)
    }
  }, []);

  useEffect(() => {
    refreshInquiriesCount();
    refreshPendingBookingsCount();
  }, [refreshInquiriesCount, refreshPendingBookingsCount]);

  return (
    <AdminContext.Provider value={{ newInquiriesCount, updatedInquiriesCount, pendingBookingsCount, refreshInquiriesCount }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdminContext = () => useContext(AdminContext);
